import React from 'react';
import PropTypes from 'prop-types';

const namespace = 'react-select2-wrapper';

/**
 * React wrapper around the jQuery select2 plugin.
 *
 * Originally vendored from react-select2-wrapper/Select2 to drop that
 * dependency and fix React 18+ compatibility (replacing deprecated lifecycle
 * methods). This is now the canonical implementation, not a temporary patch.
 * Original source: https://github.com/rkit/react-select2-wrapper/blob/v1.0.4-beta5/src/components/Select2.js
 */
class Select2 extends React.Component {
  static propTypes = {
    defaultValue: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.array,
      PropTypes.string,
    ]),
    value: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.array,
      PropTypes.string,
    ]),
    data: PropTypes.array,
    events: PropTypes.array,
    options: PropTypes.object,
    multiple: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    onSelect: PropTypes.func,
    onUnselect: PropTypes.func,
    onChange: PropTypes.func,
  }

  static defaultProps = {
    data: [],
    events: [
      [`change.${namespace}`, 'onChange'],
      [`select2:open.${namespace}`, 'onOpen'],
      [`select2:close.${namespace}`, 'onClose'],
      [`select2:select.${namespace}`, 'onSelect'],
      [`select2:unselect.${namespace}`, 'onUnselect'],
    ],
    options: {},
    multiple: false,
  }

  constructor(props) {
    super(props);
    this.el = null;
    this.forceUpdateValue = false;
    this.initialRender = true;
  }

  componentDidMount() {
    this.initSelect2(this.props);
    this.updateValue();
  }

  componentDidUpdate(prevProps) {
    this.initialRender = false;
    this.updSelect2(this.props);
    this.updateValue();
  }

  componentWillUnmount() {
    this.destroySelect2();
  }

  initSelect2(props) {
    const { options } = props;

    const $el = $(this.el);
    // fix for updating selected value when data is changing
    if (this.forceUpdateValue) {
      this.updateSelect2Value(null);
    }
    $el.select2(this.prepareOptions(options));
    this.attachEventHandlers(props);

    // FIX: Handle clear button behavior (allowClear: true option)
    // 
    // Problem: When clicking the × (clear) button in Select2 with allowClear enabled,
    // the select2:unselecting event fires, immediately followed by select2:open event,
    // which causes the dropdown to open instead of just clearing the selection.
    // 
    // Solution: Track when we're in an "unselecting" state using jQuery data().
    // When select2:open fires while unselecting, we immediately close the dropdown
    // and clean up the unselecting flag. This prevents the unwanted dropdown opening
    // while still allowing the value to be cleared.
    // 
    // This pattern is used in other parts of the codebase (e.g., preset_selector.js.coffee)
    // and is a known workaround for this Select2 behavior.

    $el.on('select2:unselecting', function () {
      // Mark that we're currently unselecting (clearing) the value
      $(this).data('unselecting', true);
    });

    $el.on('select2:open', function (e) {
      // If we're opening while in unselecting state, it means the user clicked
      // the clear button and we should prevent the dropdown from opening
      if ($(this).data('unselecting')) {
        // Close the dropdown immediately
        $(this).select2('close');
        // Clean up the flag
        $(this).removeData('unselecting');
      }
    });
  }

  updSelect2(props) {
    const prevProps = this.props;

    if (prevProps.data !== props.data) {
      this.forceUpdateValue = true;
      this.destroySelect2(false);
      this.initSelect2(props);
      return;
    }

    const { options } = props;
    const $el = $(this.el);

    if (prevProps.options !== options) {
      $el.select2(this.prepareOptions(options));
    }

    const handlerChanged = e => prevProps[e[1]] !== props[e[1]];
    if (props.events.some(handlerChanged)) {
      this.detachEventHandlers();
      this.attachEventHandlers(props);
    }
  }

  updateSelect2Value(value) {
    const $el = $(this.el);
    $el.off(`change.${namespace}`).val(value).trigger('change');

    const onChange = this.props.onChange;
    if (onChange) {
      $el.on(`change.${namespace}`, onChange);
    }
  }

  updateValue() {
    const { value, defaultValue, multiple } = this.props;
    const newValue = this.prepareValue(value, defaultValue);
    const $el = $(this.el);
    const currentValue = multiple ? $el.val() || [] : $el.val();

    if (!this.fuzzyValuesEqual(currentValue, newValue) || this.forceUpdateValue) {
      this.updateSelect2Value(newValue);
      if (!this.initialRender) {
        $el.trigger('change');
      }
      this.forceUpdateValue = false;
    }
  }

  fuzzyValuesEqual(currentValue, newValue) {
    return (currentValue === null && newValue === '') ||
      this.shallowEqual(currentValue, newValue);
  }

  shallowEqual(a, b) {
    if (a === b) return true;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    }
    return false;
  }

  destroySelect2(withCallbacks = true) {
    if (withCallbacks) {
      this.detachEventHandlers();
    }

    const $el = $(this.el);

    // Unbind clear button fix events to prevent memory leaks
    $el.off('select2:unselecting');
    $el.off('select2:open');

    $el.select2('destroy');
  }

  attachEventHandlers(props) {
    const $el = $(this.el);
    props.events.forEach(event => {
      if (typeof props[event[1]] !== 'undefined') {
        $el.on(event[0], props[event[1]]);
      }
    });
  }

  detachEventHandlers() {
    const $el = $(this.el);
    this.props.events.forEach(event => {
      if (typeof this.props[event[1]] !== 'undefined') {
        $el.off(event[0]);
      }
    });
  }

  prepareValue(value, defaultValue) {
    const issetValue = typeof value !== 'undefined' && value !== null;
    const issetDefaultValue = typeof defaultValue !== 'undefined';

    if (!issetValue && issetDefaultValue) {
      return defaultValue;
    }
    return value;
  }

  prepareOptions(options) {
    const opt = options;
    if (typeof opt.dropdownParent === 'string') {
      opt.dropdownParent = $(opt.dropdownParent);
    }
    return opt;
  }

  isObject(value) {
    const type = typeof value;
    return type === 'function' || (value && type === 'object') || false;
  }

  makeOption(item) {
    if (this.isObject(item)) {
      const { id, text, ...itemParams } = item;
      return (<option key={`option-${id}`} value={id} {...itemParams}>{text}</option>);
    }

    return (<option key={`option-${item}`} value={item}>{item}</option>);
  }

  render() {
    const { data, value, ...props } = this.props;

    delete props.options;
    delete props.events;
    delete props.onOpen;
    delete props.onClose;
    delete props.onSelect;
    delete props.onChange;
    delete props.onUnselect;

    return (
      <select {...props} ref={(el) => { this.el = el; }}>
        {data.map((item, k) => {
          if (this.isObject(item) && this.isObject(item.children)) {
            const { children, text, ...itemParams } = item;
            return (
              <optgroup key={`optgroup-${k}`} label={text} {...itemParams}>
                {children.map((child) => this.makeOption(child))}
              </optgroup>
            );
          }
          return this.makeOption(item);
        })}
      </select>
    );
  }
}

export default Select2;
